import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  CollaborationPack,
  CollaborationParticipant,
  CollaborationReadResult,
  CollaborationRole,
  CollaborationRoundResult,
  CollaborationInviteTarget,
  ManualReminderResult,
  PresenceContext,
  PresenceSnapshot,
  ReviewSubmissionPlan,
  SubmitForReviewRequest,
} from "../../model/collaboration/collaboration.model";
import { CollaboratorPreference, NotificationPreferenceUpdate, NotificationPreferenceView } from "../../model/collaboration/notification.model";
import { createUrl } from "../api.util";

@Injectable({ providedIn: "root" })
export class CollaborationApi {
  private readonly path = "cfep-collaboration";

  constructor(private readonly httpClient: HttpClient) {}

  inviteNamed(request: {firstName:string;lastName:string;email:string;productIds:number[];roles:CollaborationRole[];reviewStart?:string;reviewEnd?:string}): Observable<{userId:number;created:boolean;queued:number}> {
    return this.httpClient.post<{userId:number;created:boolean;queued:number}>(createUrl(this.path,"invite"),request);
  }
  adminAssignments():Observable<{id:number;formGroupId:number;role:string;status:string;name:string;survey:string;invitationId:number|null;deliveryStatus:string}[]>{return this.httpClient.get<{id:number;formGroupId:number;role:string;status:string;name:string;survey:string;invitationId:number|null;deliveryStatus:string}[]>(createUrl(this.path,"admin-assignments"));}
  // CFEP_ANIS5: an Unconfirmed invitation is resent only with the Admin's acknowledgement that it may arrive twice.
  resendInvitation(invitationId:number,acknowledgePossibleDuplicate=false):Observable<{status:string}>{return this.httpClient.post<{status:string}>(createUrl(this.path,"resend-invitation"),acknowledgePossibleDuplicate?{invitationId,acknowledgePossibleDuplicate:true}:{invitationId});}
  confirmInvitationDelivered(invitationId:number):Observable<{status:string}>{return this.httpClient.post<{status:string}>(createUrl(this.path,"confirm-invitation-delivered"),{invitationId});}
  assignedCatalog():Observable<{id:number;name:string;participantId:number;role:string;status:string}[]> {
    return this.httpClient.get<{id:number;name:string;participantId:number;role:string;status:string}[]>(createUrl(this.path,"assigned-catalog"));
  }
  acceptProduct(formGroupId:number):Observable<{accepted:boolean}>{
    return this.httpClient.put<{accepted:boolean}>(createUrl(this.path,formGroupId,"accept-product"),{});
  }
  // D3 NP-256: the signed-in person's own notification settings for this Survey Product.
  preferences(id:number):Observable<NotificationPreferenceView>{return this.httpClient.get<NotificationPreferenceView>(createUrl(this.path,id,"preferences"));}
  savePreferences(id:number,update:NotificationPreferenceUpdate):Observable<NotificationPreferenceView>{return this.httpClient.put<NotificationPreferenceView>(createUrl(this.path,id,"preferences"),update);}
  /** D3 NP-256 AC5: CFEP Admin only, read-only. */
  collaboratorPreferences(id:number):Observable<CollaboratorPreference[]>{return this.httpClient.get<CollaboratorPreference[]>(createUrl(this.path,id,"collaborator-preferences"));}
  participants(id:number):Observable<{id:number;userId:number;name:string;role:string;status:string}[]>{return this.httpClient.get<{id:number;userId:number;name:string;role:string;status:string}[]>(createUrl(this.path,id,"participants"));}
  assignedProducts(): Observable<number[]> {
    return this.httpClient.get<number[]>(createUrl(this.path, "assigned-products"));
  }
  document(formGroupId: number, documentId: number): Observable<string> {
    return this.httpClient.get<string>(createUrl(this.path, formGroupId, "document", documentId));
  }
  pack(formGroupId: number): Observable<CollaborationPack> {
    return this.httpClient.get<CollaborationPack>(createUrl(this.path, formGroupId, "pack"));
  }
  returnForChanges(formGroupId: number, roundId: number, participantId: number, reason: string): Observable<CollaborationRoundResult> {
    return this.httpClient.put<CollaborationRoundResult>(createUrl(this.path, formGroupId, "return-for-changes"), { roundId, participantId, reason });
  }
  current(formGroupId: number): Observable<CollaborationReadResult> {
    return this.httpClient.get<CollaborationReadResult>(createUrl(this.path, formGroupId, "current"));
  }

  history(formGroupId: number, beforeId?: number): Observable<CollaborationReadResult> {
    return this.httpClient.get<CollaborationReadResult>(createUrl(this.path, formGroupId, "history"), {params: beforeId ? {limit:50,beforeId} : {limit:50}});
  }

  acceptInvitation(formGroupId: number, roundId: number, participantId: number): Observable<CollaborationParticipant> {
    return this.httpClient.put<CollaborationParticipant>(createUrl(this.path, formGroupId, "accept-invitation"), { roundId, participantId });
  }

  revoke(formGroupId: number, participantId: number): Observable<CollaborationParticipant> {
    return this.httpClient.put<CollaborationParticipant>(createUrl(this.path, formGroupId, "revoke"), { participantId });
  }

  restore(formGroupId: number, participantId: number, role: CollaborationRole): Observable<CollaborationParticipant> {
    return this.httpClient.put<CollaborationParticipant>(createUrl(this.path, formGroupId, "restore"), { participantId, role });
  }

  manualReminder(formGroupId: number, roundId: number, approverParticipantId: number): Observable<ManualReminderResult> {
    return this.httpClient.put<ManualReminderResult>(createUrl(this.path, formGroupId, "manual-approval-reminder"), { roundId, approverParticipantId });
  }

  /** D3 NP-301 AC8: Admin only; the reviewer must still have an outstanding review in the current cycle. */
  manualReviewReminder(formGroupId: number, roundId: number, reviewerParticipantId: number): Observable<{ roundId: number; reviewerParticipantId: number; sent: boolean }> {
    return this.httpClient.put<{ roundId: number; reviewerParticipantId: number; sent: boolean }>(createUrl(this.path, formGroupId, "manual-review-reminder"), { roundId, reviewerParticipantId });
  }

  submitReview(formGroupId: number, roundId: number, participantId: number): Observable<CollaborationRoundResult> {
    return this.httpClient.put<CollaborationRoundResult>(createUrl(this.path, formGroupId, "submit-review"), { roundId, participantId });
  }

  approve(formGroupId: number, roundId: number, approverParticipantId: number): Observable<CollaborationRoundResult> {
    return this.httpClient.put<CollaborationRoundResult>(createUrl(this.path, formGroupId, "approve"), { roundId, approverParticipantId });
  }

  // D3 Batch 1 (Admin only on the server).
  reviewPlan(formGroupId: number): Observable<ReviewSubmissionPlan> {
    return this.httpClient.get<ReviewSubmissionPlan>(createUrl(this.path, formGroupId, "review-plan"));
  }

  submitForReview(formGroupId: number, request: SubmitForReviewRequest): Observable<CollaborationRoundResult> {
    return this.httpClient.post<CollaborationRoundResult>(createUrl(this.path, formGroupId, "submit-for-review"), request);
  }

  adminApprove(formGroupId: number, roundId: number): Observable<CollaborationRoundResult> {
    return this.httpClient.put<CollaborationRoundResult>(createUrl(this.path, formGroupId, "admin-approve"), { roundId });
  }

  adminReturn(formGroupId: number, roundId: number, reason: string): Observable<CollaborationRoundResult> {
    return this.httpClient.put<CollaborationRoundResult>(createUrl(this.path, formGroupId, "admin-return"), { roundId, reason });
  }

  adminOverride(formGroupId: number, roundId: number, reason: string): Observable<CollaborationRoundResult> {
    return this.httpClient.put<CollaborationRoundResult>(createUrl(this.path, formGroupId, "admin-override"), { roundId, reason });
  }

  // D3 NP-300: presence heartbeat (also carries the live-update revisions) and leave.
  presence(formGroupId: number, sessionKey: string, context: PresenceContext, joining = false): Observable<PresenceSnapshot> {
    return this.httpClient.put<PresenceSnapshot>(createUrl(this.path, formGroupId, "presence"), joining ? { sessionKey, context, joining: true } : { sessionKey, context });
  }

  leavePresence(formGroupId: number, sessionKey: string): Observable<{ left: boolean }> {
    return this.httpClient.put<{ left: boolean }>(createUrl(this.path, formGroupId, "presence", "leave"), { sessionKey });
  }

  /** NP-302 AC5 (Admin only): the originating Survey Product by id and whether it can take new collaborators. */
  inviteTarget(formGroupId: number): Observable<CollaborationInviteTarget> {
    return this.httpClient.get<CollaborationInviteTarget>(createUrl(this.path, formGroupId, "invite-target"));
  }

  /** NP-343 AC8: which of these Survey Products show Override Applied (empty for anyone who is not an internal user). */
  overrideIndicators(formGroupIds: number[]): Observable<number[]> {
    return this.httpClient.get<number[]>(createUrl(this.path, "override-indicators"), { params: { ids: formGroupIds.join(",") } });
  }
}
